import { UIService } from "../api/UIService";
import { Vec2 } from "../models/SyntheticMap";
import * as THREE from "three";
import { SiteAnchorVisual } from "../visuals/SiteAnchorVisual";
export function normalizeAngleToPi(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

export function constrain(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function isClose(a: number, b: number, epsilon = 0.0001): boolean {
  return Math.abs(a - b) < epsilon;
}

export function toRad(degrees: number, precision = 3): number {
  // Convert to radians
  const radians = degrees * (Math.PI / 180);
  // Round to the specified decimal points
  return Math.round(radians * Math.pow(10, precision)) / Math.pow(10, precision);
}

export function toDeg(radians: number, precision = 3): number {
  // Convert to degrees
  const degrees = radians * (180 / Math.PI);
  // Round to the specified decimal points
  return Math.round(degrees * Math.pow(10, precision)) / Math.pow(10, precision);
}




export async function generatePngMap(
  uiService: UIService,
  size: number,
  resolution: number = 0.05
): Promise<ArrayBuffer> {
  const width = size;
  const height = size;
  const pixel_width = width / resolution;
  const pixel_height = height / resolution;
  var canvas = document.createElement("canvas");
  canvas.width = pixel_width;
  canvas.height = pixel_height;
  const ctx = canvas.getContext("2d");

  const convertToMapCoordinates = (x: number, y: number): Vec2 => {
    return {
      x: Math.round(x / resolution) + Math.round(pixel_height / 2),
      y: Math.round(y / resolution) + Math.round(pixel_width / 2),
    }
  }

  if (ctx) {
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, pixel_width, pixel_height);
    for (const comp of uiService.components) {
      comp.renderMap(ctx, resolution, convertToMapCoordinates);
    };
  }

  // create PNG file
  const png = await canvasToBlob(canvas, 'image/png', 1);
  // console.log(png);

  // TODO: send the image to robocore
  const buffer = await blobToArrayBufferUsingFileReader(png);
  return buffer;
}

export function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string = 'image/png',
  quality?: number
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob: Blob | null) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Canvas is empty'));
      }
    }, type, quality);
  });
}

export function blobToArrayBufferUsingFileReader(blob: Blob): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Unexpected result type'));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

export function getLineLength(start: THREE.Vector3, end: THREE.Vector3) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dz = end.z - start.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function aToV3(a: number[]): THREE.Vector3 {
  return new THREE.Vector3(a[0], a[1], a[2]);
}
export function aToEuler(a: number[]): THREE.Euler {
  return new THREE.Euler(a[0], a[1], a[2]);
}

export function assignV3(v: THREE.Vector3, a: number[] | number): void {
  if (typeof a === 'number') {
    v.x = a;
    v.y = a;
    v.z = a;
    return;
  } else if (a.length === 1) {
    v.x = a[0];
    v.y = a[0];
    v.z = a[0];
  } else if (a.length === 3) {
    v.x = a[0];
    v.y = a[1];
    v.z = a[2];
  }
}

export function assignEuler(v: THREE.Euler, a: number[]): void {
  v.x = a[0];
  v.y = a[1];
  v.z = a[2];
}

export function v3ToA(v: THREE.Vector3): number[] {
  return [v.x, v.y, v.z];
}

export function isPointInsidePolygon(point: Vec2, polygonPoints: Vec2[]): boolean {
  const x = point.x;
  const y = point.y;
  let inside = false;

  const n = polygonPoints.length;
  if (n < 3) return false; // Not a valid polygon

  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygonPoints[i].x;
    const yi = polygonPoints[i].y;
    const xj = polygonPoints[j].x;
    const yj = polygonPoints[j].y;

    // Check if the ray from (x, y) to the right intersects the edge
    const intersect =
      yi > y !== yj > y && // y is between the y-coordinates of the edge
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi; // x is to the left of the intersection

    if (intersect) inside = !inside;
  }

  return inside;
}